<template>
  <Transition name="nested">
    <div v-if="isShowDraft" class="base-modal">
      <div class="base-modal__wrapper">
        <div class="base-modal__container relative">
          <div class="relative base-modal__content py-4 flex flex-col">
            <!--Header-->
            <div class="base-modal__header d-flex flex-row no-wrap">
              <span class="base-modal__btn base-modal__btn_close text-[#A1A1AA]" @click="closeDrafts">Cancel</span>
              <h2
                class="text-[16px] text-foregroundPrimary mx-auto"
              >
                Drafts
              </h2>
            </div>
            <!--Body-->
            <div class="modal-scroll-body overflow-auto px-5 pt-4">
              <div v-if="isLoading" class="w-fit mx-auto overflow-hidden">
                <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="5" fill="transparent"
                                animationDuration=".8s" aria-label="Custom ProgressSpinner"/>
              </div>
              <div v-if="!draftPosts[0] && !isLoading" class="flex flex-col">
                <span class="text-[24px]">Hold that thought</span>
                <span>Not ready to share just yet? Save it to drafts and post whenever you're ready.</span>
              </div>
              <div v-if="draftPosts[0] && !isLoading" class="max-h-[500px]">
                <TransitionGroup name="list">
                  <div v-for="post in draftPosts" :key="post.id" class="drafts__item mb-3 p-2">
                    <div class="w-full drafts__inner cursor-pointer mr-2" @click="addDraftDataToCreatePost(post)">
                      <span v-if="post.title" class="drafts__text_bold mr-2">{{ trimText(post.title, !isMobile ? 15: 10) }}</span>
                      <span v-if="post.text" class="drafts__text" v-html="trimText(post?.text?.replace(/<[^>]+>/g, '').trim(), !isMobile ? 60: post.title ? 15: 30)"></span>
                    </div>
                    <div class="flex items-center">
                      <div v-for="file in post.attachedFiles" :key="file.id" class="mr-2">
                        <img v-if="file.mimetype.split('/')[0] !== 'video'" :src="BaseURL + file.storageId" alt="image" class="block max-h-[42px] object-contain">
                        <div v-else class="flex items-center justify-center mr-2">
                          <Video />
                        </div>
                      </div>
                    </div>
                    <div class="drafts__remove flex items-center">
                      <Trash class="mr-[3px] cursor-pointer min-w-[20px]" @click="deleteDraftPost(post.id)" />
                    </div>
                  </div>
                </TransitionGroup>
              </div>
            </div>
            <!--Footer-->
            <div class="base-modal__footer flex flex-col pl-5 pr-4">

            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import ProgressSpinner from 'primevue/progressspinner';
import Trash from '@/components/Icons/Trash.vue';
import Video from '@/components/Icons/Video.vue';
import { BaseURL } from '@/global/baseUrls.js';
import { trimText } from '../../utils/textUtils';

const store = useStore();
const toast = useToast();

const setShowDraft = (value) => store.dispatch("showModalDraft", value);
const getDraftPosts = () => store.dispatch("fetchDraftPosts");
const setDataDraftPost = (post) => store.dispatch("addDataDraftPost", post);
const deleteDraft = (id) => store.dispatch("deleteDraftPost", id);
const isShowDraft = computed(() => store.getters.getShowModalDraftStatus);
const draftPosts = computed(() => store.getters.getDraftPosts || []);
const isMobile = ref(window.innerWidth < 1023);
const isLoading = ref(false);

const closeDrafts = () => {
  setShowDraft(false);
};

watch(
  () => isShowDraft.value,
  () => {
    if(isShowDraft.value) {
      asyncGetDrafts();
    }
  }
);

const addDraftDataToCreatePost = (post) => {
  setDataDraftPost(post);
  setShowDraft(false);
}

const asyncGetDrafts = async () => {
  isLoading.value = true;
  await getDraftPosts();
  isLoading.value = false;
}

const deleteDraftPost = async (id) => {
  let res = await deleteDraft(id);
  if(res.ok) {
    toast.add({
      severity: "success",
      summary: "Draft Deleted",
      detail: "Your unsent draft has been deleted.",
      life: 3000
    });
  }
}

</script>

<style lang="scss" scoped>
.drafts__item {
  position: relative;
  display: flex;
  justify-content: space-between;
  justify-items: center;
  width: 100%;
  height: fit-content;
  background: var(--background-secondary);
  @media (max-width:1023px) {
    flex-direction: column;
    padding-right: 40px;
  }
}

.drafts__remove {
  @media (max-width:1023px) {
    position: absolute;
    right: 10px;
    top: 0;
    bottom: 0;
  }
}

.drafts__text {
  font-size: 16px;
  color: var(--foreground-primary);
  font-weight: 400;
  &_bold {
    font-weight: bold;
  }
}

.nested-enter-active, .nested-leave-active {
	transition: all 0.3s ease-in-out;
}

.nested-leave-active {
  transition-delay: 0.25s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-100px);
  opacity: 0;
}

.nested-enter-active .inner,
.nested-leave-active .inner { 
  transition: all 0.3s ease-in-out;
}

.nested-enter-active .inner {
	transition-delay: 0.25s;
}

.nested-enter-from .inner,
.nested-leave-to .inner {
  transform: translateX(10px);
  opacity: 0.001;
}

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
</style>